import { registerDestructor } from '@ember/destroyable';
import { type Registry as Services, service } from '@ember/service';
import { RouterProvider } from 'compatibility-router';
import Modifier from 'ember-modifier';
import createCompatibilityRouter from 'lumen/utils/mount-react/create-compatibility-router';
import createIntl from 'lumen/utils/mount-react/create-intl';
import { type ComponentType, createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { RawIntlProvider } from 'react-intl';

interface MountReactSignature {
  Args: { Positional: [component: ComponentType] };
  Element: Element;
}

/**
 * The single seam between Ember and the shared React component packages: mount
 * a React component (unmounting it on teardown), backing compatibility-router
 * with Ember's RouterService and react-intl with lumen's translations, so the
 * package needs neither routing props nor a Lokalise project of its own.
 */
export default class MountReact extends Modifier<MountReactSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  #root?: Root;

  modify(
    element: Element,
    [component]: MountReactSignature['Args']['Positional'],
  ): void {
    if (!this.#root) {
      this.#root = createRoot(element);
      registerDestructor(this, () => this.#root?.unmount());
    }

    // Render React synchronously in the DOM before Ember's render
    // settles. By default React renders async and can cause flashes
    // of white content or test flakiness. Using flushSync trades
    // performance for that accuracy.
    flushSync(() => {
      this.#root?.render(
        createElement(
          RawIntlProvider,
          { value: createIntl(this.intl) },
          createElement(RouterProvider, {
            ...createCompatibilityRouter(this.router),
            children: createElement(component),
          }),
        ),
      );
    });
  }
}
